import { ApolloClient } from 'apollo-client';
import { BatchHttpLink } from 'apollo-link-batch-http';
import { setContext } from 'apollo-link-context';
import { execute, ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
  defaultDataIdFromObject,
  // EsLint cannot find this import for some reason.
  // eslint-disable-next-line import/no-unresolved, import/extensions
} from 'apollo-cache-inmemory';
import { createUploadLink } from 'apollo-upload-client';
import fetch from 'node-fetch';
import Raven from 'raven-js';
import { Logger } from '../../../utils/logger';

// Apollo cache and fragmentMatcher
export const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: {
    __schema: {
      types: [
        {
          kind: 'UNION',
          name: 'BookingFormField',
          possibleTypes: [
            { name: 'BookingFormAddressField' },
            { name: 'BookingFormCheckBoxesField' },
            { name: 'BookingFormDateField' },
            { name: 'BookingFormGroupSelectField' },
            { name: 'BookingFormEmailField' },
            { name: 'BookingFormHeadingField' },
            { name: 'BookingFormRadioButtonsField' },
            { name: 'BookingFormSelectField' },
            { name: 'BookingFormTextAreaField' },
            { name: 'BookingFormTextField' },
            { name: 'BookingFormReassuranceBoxField' },
          ],
        },
      ],
    },
  },
});
export default (authService) => {
  const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
    // Currently, graphql-tools remote executable schemas do not pass through
    // the custom error code property so we are having to use the less reliable error message
    if (
      graphQLErrors &&
      graphQLErrors.map((e) => e.message).includes('Access denied')
    ) {
      if (__CLIENT__) {
        /* eslint-disable no-unused-expressions */
        authService?.logout?.();
      }
    }

    if (__DEVELOPMENT__) {
      if (networkError) {
        Logger.error(`[Network error]: ${networkError}`, networkError);
      }
    }

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        Logger.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, operation: ${operation.operationName}`,
        );
        Raven.captureException(new Error(message), {
          tags: { operation: operation.operationName },
        });
      });
    }

    if (networkError) {
      Raven.captureException(new Error(networkError.message), {
        tags: { operation: operation.operationName },
      });
    }
  });

  const middlewareLink = setContext(async (request) => {
    const headers = {};
    const primaryIdentityId = authService?.getPrimaryIdentityId?.();
    if (primaryIdentityId) {
      headers['User-Id'] = primaryIdentityId;
    }
    const idToken = authService?.getIdToken?.();
    if (idToken) {
      headers.authorization = `Bearer ${idToken}`;
    }
    if (request.variables) {
      const { authorizationOverrideToken } = request.variables;
      if (authorizationOverrideToken) {
        headers.authorization = `Bearer ${authorizationOverrideToken}`;
      }
    }
    return { headers };
  });

  const loggingLink = new ApolloLink((operation, forward) => {
    const { operationName, variables } = operation;
    const group = `GraphQL: ${operationName}`;
    Logger.groupCollapsed(group);
    Logger.log(variables);
    Logger.groupEnd(group);
    return forward(operation);
  });

  // All non terminating links that are added to other terminating link chains.
  const universalChain = errorLink.concat(loggingLink).concat(middlewareLink);

  const batchChain = universalChain.concat(
    new BatchHttpLink({ uri: GQL_ROOT, fetch }),
  );

  // TODO(ryan@): If/when another service needs file upload change this to be
  //              a factory that takes a url.
  const uploadChain = universalChain.concat(
    createUploadLink({ uri: USER_ROOT_EXTERNAL, fetch }),
  );

  // Sends the request to an upload link or batch link depending on useUploadLink provided
  // in the query context.
  //
  // Example:
  //   graphql(SomeMutation, {
  //     name: 'SomeMutation',
  //     options: {
  //       context: {
  //         useUploadLink: true,
  //       },
  //     },
  //   });
  //
  // Calling execute kills the context set in the link chain up to this point, so both
  // chains must include all middleware links.
  const redirectLink = new ApolloLink((operation) => {
    const link = operation.getContext().useUploadLink
      ? uploadChain
      : batchChain;
    return execute(link, operation);
  });

  const cache = new InMemoryCache({
    fragmentMatcher,
    dataIdFromObject: (object) => {
      switch (object.__typename) {
        case 'LegacyRaceKeyDetail':
          return JSON.stringify(object);
        case 'StartlistExtraField':
          return JSON.stringify(object);
        default:
          return defaultDataIdFromObject(object); // fall back to default handling
      }
    },
  }).restore(__SERVER__ ? {} : window.__APOLLO_STATE__);

  // Apollo Links

  return new ApolloClient({
    ssrMode: true,
    link: redirectLink,
    cache,
    fetch,
  });
};
